import { Controller } from "@hotwired/stimulus";
import { createCalendar, createViewWeek } from "@schedule-x/calendar";
import { createEventModalPlugin } from "@schedule-x/event-modal";
import { createEventsServicePlugin } from "@schedule-x/events-service";

export default class extends Controller {
  static values = {
    events: Array,
    url: String,
    dayBoundaries: Object,
  };

  connect() {
    // Apply colors per space so bookings from the same space share a hue
    this.palette = [
      "#1e40af", // blue-800
      "#10b981", // emerald-500
      "#ea580c", // orange-600
      "#b91c1c", // red-700
      "#7c3aed", // violet-600
      "#0e7490", // cyan-700
      "#f59e0b", // amber-500
      "#059669", // green-600
    ];

    this.spaceColors = {};
    this.colorFor = (spaceId) => {
      if (!spaceId) return "#3b82f6"; // default blue
      if (!this.spaceColors[spaceId]) {
        const index = Object.keys(this.spaceColors).length % this.palette.length;
        this.spaceColors[spaceId] = this.palette[index];
      }
      return this.spaceColors[spaceId];
    };

    // Helper to decorate events with consistent colours
    const decorateEvents = (evts) =>
      (evts || []).map((evt) => ({
        ...evt,
        color: this.colorFor(evt.spaceId),
      }));

    this.eventsServicePlugin = createEventsServicePlugin();
    this.eventModal = createEventModalPlugin();

    // Build calendar configuration
    const calendarConfig = {
      selectedView: "week",
      weekOptions: {
        gridHeight: 600,
        eventWidth: 50,
      },
      views: [createViewWeek({ weekStartsOn: 0 })],
      // Respect provider operating hours if supplied by Rails view
      ...(this.hasDayBoundariesValue ? { dayBoundaries: this.dayBoundariesValue } : {}),
      events: decorateEvents(this.eventsValue),
      callbacks: {
        // Whenever the visible range changes (arrow navigation, date picker, view switch), fetch fresh events
        onRangeUpdate: (range) => {
          this.loadEvents(range);
        },
      },
    };

    // Initialize Schedule-X with plugins
    this.calendar = createCalendar(calendarConfig, [this.eventsServicePlugin, this.eventModal]);

    // Initial render
    this.calendar.render(this.element);
  }

  disconnect() {
    if (this.calendar && this.calendar.destroy) this.calendar.destroy();
  }

  loadEvents(range) {
    if (!this.hasUrlValue) return;

    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set("start", range.start);
    url.searchParams.set("end", range.end);

    fetch(url.toString(), {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((r) => r.json())
      .then((data) => {
        const decorated = data.map((e) => ({
          ...e,
          color: this.colorFor(e.spaceId),
        }));

        // this.calendar.updateEvents(decorated);
        this.eventsServicePlugin.set(decorated);
      })
      .catch((err) => console.error("Error loading calendar events", err));
  }
}
